import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, FileText, Edit } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'

const defaultPages = [
  { slug: 'about', name: 'About Us', description: 'Company story and values' },
]

export default function AdminPages() {
  const { isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [pages, setPages] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchPages()
  }, [isAuthenticated, isAdmin])

  const fetchPages = async () => {
    try {
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .order('page_slug')

      if (error) throw error

      // Merge with default pages to show all available pages
      const existingPages = data || []
      const mergedPages = defaultPages.map(defaultPage => {
        const existing = existingPages.find(p => p.page_slug === defaultPage.slug)
        return {
          ...defaultPage,
          hasContent: !!existing,
          content: existing
        }
      })

      setPages(mergedPages)
    } catch (error) {
      console.error('Error fetching pages:', error)
      // If table doesn't exist yet, just show default pages
      setPages(defaultPages.map(p => ({ ...p, hasContent: false })))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading pages...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-display-md text-pp-ink">
              Page Content
            </h1>
            <p className="text-pp-stone">Manage your website pages</p>
          </div>
        </div>

        <div className="grid gap-4">
          {pages.map((page) => (
            <motion.div
              key={page.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 border border-pp-sand/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pp-sage/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-pp-sage" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-pp-ink">
                      {page.name}
                    </h3>
                    <p className="text-sm text-pp-stone">{page.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {page.hasContent ? (
                    <span className="px-3 py-1 bg-pp-sage/10 text-pp-sage text-xs font-medium rounded-full">
                      Customized
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-pp-sand/50 text-pp-stone text-xs font-medium rounded-full">
                      Default
                    </span>
                  )}
                  <Link
                    to={`/admin/pages/${page.slug}`}
                    className="p-2 hover:bg-pp-sand/30 rounded-lg transition-colors"
                  >
                    <Edit className="w-5 h-5 text-pp-charcoal" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
